import type { ClientTool } from "./ClientTools";

/**
 * Approval modes that control when a client-side tool requires user
 * confirmation before executing.
 */
export enum ApprovalMode {
  /** Always show a confirmation modal before executing the tool. */
  REQUIRE_APPROVAL = "require_approval",
  /** Show a confirmation modal when the Auto toggle is OFF; auto-execute when ON. */
  FOLLOW_AUTO = "follow_auto",
  /** Always execute immediately without showing a confirmation modal. */
  AUTO_EXECUTE = "auto_execute",
}

/**
 * Resolve the effective approval mode for a tool, taking into account
 * the user's stored preference and the tool's allowable modes.
 *
 * Falls back to the tool's default if:
 * - No user preferences exist
 * - The stored preference is not in the tool's allowable modes
 *   (e.g., after an update restricted options)
 */
export function resolveApprovalMode(
  tool: ClientTool,
  userPreferences: Record<string, ApprovalMode> | null | undefined,
): ApprovalMode {
  const stored = userPreferences?.[tool.spec.name];

  if (stored && tool.allowableApprovalModes.includes(stored)) {
    return stored;
  }

  return tool.defaultApprovalMode;
}

/**
 * Determine whether a tool invocation needs user approval given the
 * resolved approval mode and the current Auto toggle state.
 */
export function needsApproval(
  mode: ApprovalMode,
  autoEnabled: boolean,
): boolean {
  switch (mode) {
    case ApprovalMode.REQUIRE_APPROVAL:
      return true;
    case ApprovalMode.FOLLOW_AUTO:
      return !autoEnabled;
    case ApprovalMode.AUTO_EXECUTE:
      return false;
  }
}
